import { Checkbox, Collapse, IconButton, Tooltip, Typography } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Dispatch } from '@reduxjs/toolkit';
import { DialogVariantEnum } from 'enums/dialogVariant';
import { MenuItemsEnum } from 'enums/menuItemsEnum';
import { StatusEnum } from 'enums/status';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch } from 'store';
import { dialogClose, dialogOpen } from 'store/dialog/dialog.actions';
import { DialogOpenPayloadJobs, IDialogOpenPayload } from 'store/dialog/dialog.interface';
import { checkProject } from 'store/projects/projects.actions';
import { IProjectCheckPayload, LanguageCheckArray, LanguageErrorArray } from 'store/projects/projects.interface';
import { notify } from 'utils/handlers/notificationHandler';
import { ProjectDataDTO, ProjectDTO, ProjectTargetLanguageDTO, XTMJobStatus } from 'utils/restApplicationClient';
import { isInIFrame } from 'utils/services/kenticoSdk.service';
import { TableCell, TableRow } from '../TranslationProgress.styled';
import { ExpandButton, IFrameStatusContainer, NameCell, ProgressBar, StatusCell } from './ProjectRow.styled';
import { statusColorMap } from './statusColorMap';

interface IProps {
  checkProjectArray?: {
    checked: boolean;
    indeterminate?: boolean;
    languages: LanguageCheckArray;
  };
  projectErrorArray?: {
    error: string;
    languages: LanguageErrorArray;
  };
  project: ProjectDTO;
  handleMenuOpen: (
    menuItem: MenuItemsEnum,
    projectInfo: IDialogOpenPayload['projectInfo'],
  ) => (event: React.MouseEvent<HTMLElement>) => void;
}

interface IDispatchProps {
  dialogOpen: (payload: IDialogOpenPayload) => AppDispatch;
  dialogClose: () => AppDispatch;
  checkProject: (payload: IProjectCheckPayload) => AppDispatch;
}

interface IState {
  firstStageOpen: boolean;
  secondStageOpen: boolean[];
}

type PropType = WithTranslation & IProps & IDispatchProps;

export class ProjectRow extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);

    const {
      project: { targetLanguages },
    } = this.props;

    this.state = {
      firstStageOpen: false,
      secondStageOpen: targetLanguages.map(() => false),
    };
  }

  handleExpand = (index?: number) => (): void => {
    const { firstStageOpen, secondStageOpen } = this.state;

    if (index === undefined) {
      this.setState({
        firstStageOpen: !firstStageOpen,
      });
    } else {
      const newSecondStageOpen: boolean[] = [...secondStageOpen];
      newSecondStageOpen[index] = !newSecondStageOpen[index];

      this.setState({
        secondStageOpen: newSecondStageOpen,
      });
    }
  };

  handleIdClick = (id: number, variant: 'JOB' | 'PROJECT') => (): void => {
    // eslint-disable-next-line promise/catch-or-return
    navigator.clipboard.writeText(id.toString()).then(() => {
      notify(`successmessage.${variant}Copied`, 'success');
    });
  };

  getDialogVariant = (error?: string): DialogVariantEnum | undefined => {
    let variant;

    switch (error) {
      case 'errormessage.job.content': {
        variant = DialogVariantEnum.CONTENT_CHANGED;
        break;
      }
      case 'errormessage.kontent.notfound': {
        variant = DialogVariantEnum.CONTENT_REMOVED;
        break;
      }
      case 'errormessage.kontent.noitemsupported': {
        variant = DialogVariantEnum.GENERIC_ERROR;
        break;
      }
      case 'errormessage.job.deleted':
      case 'errormessage.job.projectcancelled':
      case 'errormessage.job.cancelled': {
        variant = DialogVariantEnum.PROJECT_CANCELLED;
        break;
      }
      case 'errormessage.job.importtokontentunsuccessful': {
        variant = DialogVariantEnum.WORKFLOW;
        break;
      }
      case 'errormessage.kontent.someitemspublished': {
        variant = DialogVariantEnum.PARTIAL_WORKFLOW;
        break;
      }
      case 'errormessage.kontent.uploadtranslationvalidationerror': {
        variant = DialogVariantEnum.VALIDATION_ERROR;
        break;
      }
      case 'errormessage.job.downloadunsuccessful': {
        variant = DialogVariantEnum.DOWNLOAD_UNSUCCESSFUL;
        break;
      }
      case 'errormessage.job.cancellationunsuccessful': {
        variant = DialogVariantEnum.CANCEL_UNSUCCESSFUL;
        break;
      }
      default: {
        variant = DialogVariantEnum.GENERIC_ERROR;
        break;
      }
    }
    return variant;
  };

  handleJobErrorClick =
    ({ id, name, status, errorMessage, errorMessagesDetails }: ProjectDataDTO) =>
    (): void => {
      const { dialogOpen } = this.props;
      const variant = this.getDialogVariant(errorMessage);

      if (variant) {
        dialogOpen({
          variant,
          projectInfo: {
            jobs: [{ id, name, status }],
          },
          errorMessage,
          errorMessagesDetails: errorMessagesDetails ?? [],
        });
      }
    };

  handleLanguageErrorClick =
    ({ language, files }: ProjectTargetLanguageDTO, projectId: string) =>
    (): void => {
      const { dialogOpen, projectErrorArray } = this.props;
      const errorMessage = projectErrorArray?.languages[language]?.error;
      const variant = this.getDialogVariant(errorMessage);
      const filesWithError = files.filter(({ errorMessage: jobErrorMessage }) => jobErrorMessage === errorMessage);
      const jobs = filesWithError.map(({ id, name, status }) => ({ id, name, status }));
      const errorMessagesDetails = filesWithError[0]?.errorMessagesDetails ?? [];

      if (variant) {
        dialogOpen({
          variant,
          projectInfo: {
            jobs,
            projectId,
            languages: [language],
          },
          errorMessage,
          errorMessagesDetails,
        });
      }
    };

  handleProjectErrorClick =
    ({ targetLanguages, id }: ProjectDTO) =>
    (): void => {
      const { dialogOpen, projectErrorArray } = this.props;
      const errorMessage = projectErrorArray?.error;
      const variant = this.getDialogVariant(errorMessage);

      if (projectErrorArray) {
        const errorLanguages = Object.entries(projectErrorArray.languages)
          .filter(([, { error }]) => error === errorMessage)
          .map(([language]) => language);
        const filesWithError = targetLanguages
          .filter(({ language }) => errorLanguages.includes(language))
          .map(({ files }) => files.filter(({ errorMessage: jobErrorMessage }) => jobErrorMessage === errorMessage))
          .flat(1);
        const jobs = filesWithError.map(({ id, name, status }) => ({ id, name, status }));
        const errorMessagesDetails = filesWithError[0]?.errorMessagesDetails ?? [];

        if (variant) {
          dialogOpen({
            variant,
            projectInfo: {
              jobs,
              projectId: id,
              languages: [],
            },
            errorMessage,
            errorMessagesDetails,
          });
        }
      }
    };

  getProjectJobs = (project: ProjectDTO): DialogOpenPayloadJobs => {
    return project.targetLanguages
      .map((language) =>
        language.files.map(({ id, status, name }) => ({
          id,
          status,
          name,
        })),
      )
      .flat(1);
  };

  getProjectLanguages = (project: ProjectDTO): Array<string> => {
    return project.targetLanguages.map(({ language }) => language);
  };

  getLanguageJobs = (language: ProjectTargetLanguageDTO): DialogOpenPayloadJobs => {
    return language.files
      .map(({ id, status, name }) => ({
        id,
        status,
        name,
      }))
      .flat(1);
  };

  handleCheckProject =
    ({ id, targetLanguages, status: projectStatus }: ProjectDTO) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const { checkProject } = this.props;
      const checkProjectPayload = {
        projects: [
          {
            id,
            checked,
            status: projectStatus,
            languages: targetLanguages.map(({ language, files, status: languageStatus }) => ({
              id: language,
              checked,
              status: languageStatus,
              files: files.map(({ id, status: fileStatus }) => ({
                id,
                checked,
                status: fileStatus,
              })),
            })),
          },
        ],
      };

      checkProject(checkProjectPayload);
    };

  handleCheckLanguage =
    (
      { id: projectId, status: projectStatus }: ProjectDTO,
      { language, status: languageStatus, files }: ProjectTargetLanguageDTO,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const { checkProject } = this.props;
      const checkProjectPayload = {
        projects: [
          {
            id: projectId,
            checked,
            status: projectStatus,

            languages: [
              {
                id: language,
                checked,
                status: languageStatus,
                files: files.map(({ id, status: fileStatus }) => ({
                  id,
                  checked,
                  status: fileStatus,
                })),
              },
            ],
          },
        ],
      };

      checkProject(checkProjectPayload);
    };

  handleCheckFile =
    (
      { id: projectId, status: projectStatus }: ProjectDTO,
      { language, status: languageStatus }: ProjectTargetLanguageDTO,
      { id: fileId, status: fileStatus }: ProjectDataDTO,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const { checkProject } = this.props;
      const checkProjectPayload = {
        projects: [
          {
            id: projectId,
            checked,
            status: projectStatus,
            languages: [
              {
                id: language,
                checked,
                status: languageStatus,
                files: [
                  {
                    id: fileId,
                    checked,
                    status: fileStatus,
                  },
                ],
              },
            ],
          },
        ],
      };

      checkProject(checkProjectPayload);
    };

  handleDialogClose = (): void => {
    const { dialogClose } = this.props;
    dialogClose();
  };

  render(): JSX.Element {
    const { t, project, handleMenuOpen, checkProjectArray, projectErrorArray } = this.props;
    const { firstStageOpen, secondStageOpen } = this.state;

    const isExpandable = project.targetLanguages.length >= 2 || project.targetLanguages[0].files.length > 0;

    return (
      <Fragment>
        <TableRow nest={0}>
          <TableCell>
            <Checkbox
              onChange={this.handleCheckProject(project)}
              checked={checkProjectArray?.checked || false}
              indeterminate={checkProjectArray?.indeterminate}
            ></Checkbox>
          </TableCell>
          <TableCell width={16}>
            <Tooltip title={project.name} arrow>
              <Typography variant="body2" component="span" noWrap>
                <NameCell>{project.name}</NameCell>
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell width={10}>
            <Typography color="textSecondary" variant="body2" component="span">
              {project.sourceLanguageName}
            </Typography>
          </TableCell>
          <TableCell width={10}>
            <Typography color="textSecondary" variant="body2" component="span">
              {project.targetLanguages.map(({ name }, index: number) => (index ? `, ${name}` : name))}
            </Typography>
          </TableCell>
          <TableCell width={10}>
            <Tooltip title={project.customer || ''} arrow>
              <Typography color="textSecondary" variant="body2" component="span" noWrap>
                {project?.customer}{' '}
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell width={10}></TableCell>
          <TableCell width={8}>
            <Typography color="textSecondary" variant="body2" component="span">
              {project.createdAt ? moment(project.createdAt).format('DD-MM-YYYY') : ''}
            </Typography>
          </TableCell>
          <TableCell width={8}>
            <Typography color="textSecondary" variant="body2" component="span">
              {project.dueDate ? moment(project.dueDate).format('DD-MM-YYYY hh:mm') : ''}
            </Typography>
          </TableCell>
          <TableCell width={10}></TableCell>
          {isInIFrame ? (
            <TableCell width={18}>
              <ProgressBar isSuccess={project.status === StatusEnum.FINISHED || project.progress === 100}>
                <IFrameStatusContainer>
                  <Typography color="textSecondary" variant="body2" component="span">
                    <StatusCell textColor={statusColorMap[project.status as XTMJobStatus]}>
                      {t(`queue.status.${project.status}`)}
                    </StatusCell>{' '}
                  </Typography>
                  <strong>{`${project.status === StatusEnum.FINISHED ? 100 : +project.progress}%`}</strong>
                </IFrameStatusContainer>
                <progress value={project.status === StatusEnum.FINISHED ? 100 : project.progress} max="100"></progress>
              </ProgressBar>
            </TableCell>
          ) : (
            <>
              <TableCell width={10}>
                <ProgressBar isSuccess={project.status === StatusEnum.FINISHED || project.progress === 100}>
                  <progress
                    value={project.status === StatusEnum.FINISHED ? 100 : project.progress}
                    max="100"
                  ></progress>
                  <Typography color="textSecondary" variant="body2" component="span">
                    <strong>{`${project.status === StatusEnum.FINISHED ? 100 : +project.progress}%`}</strong>
                  </Typography>
                </ProgressBar>
              </TableCell>
              <TableCell width={8}>
                <Typography color="textSecondary" variant="body2" component="span">
                  <StatusCell textColor={statusColorMap[project.status as XTMJobStatus]}>
                    {t(`queue.status.${project.status}`)}
                  </StatusCell>{' '}
                </Typography>
              </TableCell>
            </>
          )}
          <TableCell>
            <IconButton
              size="small"
              onClick={this.handleProjectErrorClick(project)}
              style={{ visibility: projectErrorArray?.error ? 'visible' : 'hidden' }}
            >
              <ErrorIcon color="error" />
            </IconButton>
            <Tooltip title={<Fragment>{t('queue.labels.projectId', { id: project.xtmId })}</Fragment>}>
              <IconButton size="small" onClick={this.handleIdClick(project.xtmId, 'PROJECT')}>
                <InfoIcon color="secondary" />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              onClick={handleMenuOpen(MenuItemsEnum.PROJECT, {
                projectId: project.id,
                languages: this.getProjectLanguages(project),
                jobs: this.getProjectJobs(project),
                joinedFilesInXTM: project.joinedFilesInXTM,
              })}
            >
              <MoreVertIcon />
            </IconButton>
          </TableCell>
          {isExpandable && (
            <ExpandButton nest={0} onClick={this.handleExpand()}>
              <IconButton size="small">
                {firstStageOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </ExpandButton>
          )}
        </TableRow>

        {isExpandable && (
          <Collapse in={firstStageOpen} timeout="auto" unmountOnExit>
            {project.targetLanguages.map((targetLanguage, index: number) => (
              <Fragment key={`${project.id}-${targetLanguage.language}`}>
                <TableRow nest={1}>
                  <TableCell>
                    <Checkbox
                      onChange={this.handleCheckLanguage(project, targetLanguage)}
                      checked={checkProjectArray?.languages[targetLanguage.language]?.checked || false}
                      indeterminate={checkProjectArray?.languages[targetLanguage.language]?.indeterminate}
                    ></Checkbox>
                  </TableCell>
                  <TableCell width={16} nest={1} shorter={true}>
                    <Typography variant="body2" component="span" noWrap>
                      <Tooltip title={targetLanguage.name} arrow>
                        <NameCell>{targetLanguage.name}</NameCell>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell width={10} nest={1}>
                    <Typography color="textSecondary" variant="body2" component="span">
                      {project.sourceLanguage}
                    </Typography>
                  </TableCell>
                  <TableCell width={10} nest={1}>
                    <Typography color="textSecondary" variant="body2" component="span">
                      {targetLanguage.name}
                    </Typography>
                  </TableCell>
                  <TableCell width={10} nest={1}>
                    <Tooltip title={project.customer || ''} arrow>
                      <Typography color="textSecondary" variant="body2" component="span" noWrap>
                        {project.customer}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell width={10} nest={1}></TableCell>
                  <TableCell width={8} nest={1}></TableCell>
                  <TableCell width={8} nest={1}></TableCell>
                  <TableCell width={10} nest={1}></TableCell>
                  {isInIFrame ? (
                    <TableCell width={18} nest={1}>
                      <ProgressBar
                        isSuccess={targetLanguage.status === StatusEnum.FINISHED || targetLanguage.progress === 100}
                      >
                        <IFrameStatusContainer>
                          <Typography color="textSecondary" variant="body2" component="span">
                            <StatusCell textColor={statusColorMap[targetLanguage.status]}>
                              {t(`queue.status.${targetLanguage.status}`)}
                            </StatusCell>{' '}
                          </Typography>
                          <strong>{`${
                            targetLanguage.status === StatusEnum.FINISHED ? 100 : +targetLanguage.progress
                          }%`}</strong>
                        </IFrameStatusContainer>
                        <progress
                          value={targetLanguage.status === StatusEnum.FINISHED ? 100 : targetLanguage.progress}
                          max="100"
                        ></progress>
                      </ProgressBar>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell width={10} nest={1}>
                        <ProgressBar
                          isSuccess={targetLanguage.status === StatusEnum.FINISHED || targetLanguage.progress === 100}
                        >
                          <progress
                            value={targetLanguage.status === StatusEnum.FINISHED ? 100 : targetLanguage.progress}
                            max="100"
                          ></progress>
                          <Typography color="textSecondary" variant="body2" component="span">
                            <strong>{`${
                              targetLanguage.status === StatusEnum.FINISHED ? 100 : +targetLanguage.progress
                            }%`}</strong>
                          </Typography>
                        </ProgressBar>
                      </TableCell>
                      <TableCell width={8} nest={1}>
                        <Typography color="textSecondary" variant="body2" component="span">
                          <StatusCell textColor={statusColorMap[targetLanguage.status]}>
                            {t(`queue.status.${targetLanguage.status}`)}
                          </StatusCell>{' '}
                        </Typography>
                      </TableCell>
                    </>
                  )}

                  <TableCell nest={1}>
                    <IconButton size="small" disabled={true} style={{ visibility: 'hidden' }}>
                      <InfoIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={this.handleLanguageErrorClick(targetLanguage, project.id)}
                      style={{
                        visibility: projectErrorArray?.languages[targetLanguage.language]?.error ? 'visible' : 'hidden',
                      }}
                    >
                      <ErrorIcon color="error" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleMenuOpen(MenuItemsEnum.LANGUAGE, {
                        projectId: project.id,
                        languages: [targetLanguage.language],
                        jobs: this.getLanguageJobs(targetLanguage),
                      })}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                  {targetLanguage.files.length > 0 && (
                    <ExpandButton nest={1}>
                      <IconButton size="small" onClick={this.handleExpand(index)}>
                        {secondStageOpen[index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </ExpandButton>
                  )}
                </TableRow>
                {targetLanguage.files.length > 0 && (
                  <Collapse in={secondStageOpen[index]} timeout="auto" unmountOnExit>
                    {targetLanguage.files.map((file) => (
                      <Fragment key={`${project.id}-${targetLanguage.language}-${file.name}`}>
                        <TableRow nest={2}>
                          <TableCell>
                            <Checkbox
                              onChange={this.handleCheckFile(project, targetLanguage, file)}
                              checked={
                                checkProjectArray?.languages[targetLanguage.language]?.files[file.id]?.checked || false
                              }
                            ></Checkbox>
                          </TableCell>
                          <TableCell width={16} nest={2} shorter={true}>
                            <Typography variant="body2" component="span" noWrap>
                              <Tooltip title={file.name} arrow>
                                <NameCell>{file.name}</NameCell>
                              </Tooltip>
                            </Typography>
                          </TableCell>
                          <TableCell width={10} nest={2}>
                            <Typography color="textSecondary" variant="body2" component="span">
                              {project.sourceLanguage}
                            </Typography>
                          </TableCell>
                          <TableCell width={10} nest={2}>
                            <Typography color="textSecondary" variant="body2" component="span">
                              {targetLanguage.name}
                            </Typography>
                          </TableCell>
                          <TableCell width={10} nest={2}>
                            <Tooltip title={project.customer || ''} arrow>
                              <Typography color="textSecondary" variant="body2" component="span" noWrap>
                                {project.customer}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell width={10} nest={2}>
                            <Tooltip title={file.requester || ''} arrow>
                              <Typography color="textSecondary" variant="body2" component="span" noWrap>
                                {file.requester}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell width={8} nest={2}>
                            <Typography color="textSecondary" variant="body2" component="span">
                              {file.createdAt ? moment(file.createdAt).format('DD-MM-YYYY') : ''}
                            </Typography>
                          </TableCell>
                          <TableCell width={8} nest={2}>
                            <Typography color="textSecondary" variant="body2" component="span">
                              {file.dueDate ? moment(file.dueDate).format('DD-MM-YYYY hh:mm') : ''}
                            </Typography>
                          </TableCell>
                          <TableCell width={10} nest={2}>
                            <Typography color="textSecondary" variant="body2" component="span">
                              {file?.workflowStep || t('queue.workflowSteps.null')}
                            </Typography>
                          </TableCell>
                          {isInIFrame ? (
                            <TableCell width={18} nest={2}>
                              <ProgressBar isSuccess={file.status === StatusEnum.FINISHED || file.progress === 100}>
                                <IFrameStatusContainer>
                                  <Typography color="textSecondary" variant="body2" component="span">
                                    <StatusCell textColor={statusColorMap[file.status]}>
                                      {t(`queue.status.${file.status}`)}
                                    </StatusCell>{' '}
                                  </Typography>
                                  <strong>{`${file.status === StatusEnum.FINISHED ? 100 : +file.progress}%`}</strong>
                                </IFrameStatusContainer>
                                <progress
                                  value={file.status === StatusEnum.FINISHED ? 100 : file.progress}
                                  max="100"
                                ></progress>
                              </ProgressBar>
                            </TableCell>
                          ) : (
                            <>
                              <TableCell width={10} nest={2}>
                                <ProgressBar isSuccess={file.status === StatusEnum.FINISHED || file.progress === 100}>
                                  <progress
                                    value={file.status === StatusEnum.FINISHED ? 100 : file.progress}
                                    max="100"
                                  ></progress>
                                  <Typography color="textSecondary" variant="body2" component="span">
                                    <strong>{`${file.status === StatusEnum.FINISHED ? 100 : +file.progress}%`}</strong>
                                  </Typography>
                                </ProgressBar>
                              </TableCell>
                              <TableCell width={8} nest={2}>
                                <Typography color="textSecondary" variant="body2" component="span">
                                  <StatusCell textColor={statusColorMap[file.status]}>
                                    {t(`queue.status.${file.status}`)}
                                  </StatusCell>{' '}
                                </Typography>
                              </TableCell>
                            </>
                          )}

                          <TableCell nest={2}>
                            <IconButton
                              size="small"
                              onClick={this.handleJobErrorClick(file)}
                              style={{ visibility: file.errorMessage ? 'visible' : 'hidden' }}
                            >
                              <ErrorIcon color="error" />
                            </IconButton>
                            <Tooltip title={<Fragment>{t('queue.labels.jobId', { id: file.xtmId })}</Fragment>}>
                              <IconButton size="small" onClick={this.handleIdClick(file.xtmId, 'JOB')}>
                                <InfoIcon color="secondary" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={handleMenuOpen(MenuItemsEnum.JOB, {
                                projectId: project.id,
                                languages: [targetLanguage.language],
                                jobs: [{ id: file.id, status: file.status, name: file.name }],
                              })}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </Collapse>
                )}
              </Fragment>
            ))}
          </Collapse>
        )}
      </Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  dialogOpen: (payload: IDialogOpenPayload): AppDispatch => dispatch(dialogOpen(payload)),
  dialogClose: (): AppDispatch => dispatch(dialogClose()),
  checkProject: (payload: IProjectCheckPayload): AppDispatch => dispatch(checkProject(payload)),
});

export default connect(null, mapDispatchToProps)(withTranslation()(ProjectRow));
